import { create } from "zustand";

export interface ChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderRole: "customer" | "admin" | "ai";
  text: string;
  timestamp: number;
  status: "sending" | "sent" | "delivered" | "read";
  attachment?: { type: "image" | "file"; name: string; url: string };
}

export interface Conversation {
  id: string;
  customerId: string;
  customerName: string;
  customerAvatar?: string;
  lastMessage: string;
  lastMessageTime: number;
  unreadCount: number;
  isOnline: boolean;
  isTyping: boolean;
  aiEnabled: boolean;
  messages: ChatMessage[];
  pinned?: boolean;
  bookingId?: string;
  /** Tenant isolation — conversations belong to exactly one tenant */
  tenantId: string;
}

/* ============================================================
   Per-Tenant AI Knowledge Base
   ============================================================ */
interface TenantAIKnowledge {
  tenantName: string;
  entries: Record<string, string>;
}

/** Default tenant-specific knowledge entries.
 *  Keys match the tenant subdomain / tenant ID.
 *  Unlisted tenants get a generic fallback. */
const TENANT_AI_KNOWLEDGE: Record<string, TenantAIKnowledge> = {
  adara: {
    tenantName: "Adara Image Studio",
    entries: {
      sapaan: `Halo! 👋 Saya AI Assistant Adaraimage. Ada yang bisa saya bantu?\n\nSaya bisa bantu informasi tentang:\n📸 Paket & harga studio\n📅 Cara booking\n💳 Metode pembayaran\n📋 Status pesanan\n\nAdmin kami juga siap membantu kapan saja.`,

      paket: `📸 **Paket Studio Adaraimage:**\n\n• **Wedding Premium** — Rp 4.500.000\n  3 jam, 2 fotografer, 150+ foto edit, album cetak\n\n• **Prewedding Exclusive** — Rp 2.500.000\n  2 jam, 1 fotografer, 80+ foto edit, 2 lokasi\n\n• **Video Cinematic 3 Menit** — Rp 8.500.000\n  8 jam, 2 videografer, drone, highlight\n\n• **Self Photobox** — Rp 850.000\n  2 jam, kamera otomatis, 3 backdrop, unlimited\n\n• **Komersial Produk** — Rp 1.500.000\n  4 jam, 50+ foto edit, white background\n\nMau detail atau bantuan booking? 😊`,

      harga: `💰 **Harga mulai dari:**\n• Self Photobox: Rp 850.000\n• Prewedding: Rp 2.500.000\n• Wedding: Rp 4.500.000\n• Komersial: Rp 1.500.000\n• Video Cinematic: Rp 8.500.000\n\nTersedia add-ons: cetak frame, file RAW, drone, tambah durasi.\nBisa bayar DP 30% dulu atau lunas. 💳`,

      booking: `📅 **Cara Booking:**\n\n1. Login ke akun Anda\n2. Buka halaman Booking\n3. Pilih paket, tanggal, dan jam\n4. Tambah add-ons & voucher (jika ada)\n5. Pilih pembayaran DP 30% atau lunas\n6. Bayar via QRIS, transfer bank, atau GoPay\n7. Booking terkonfirmasi! ✅\n\nButuh bantuan? Admin kami siap membantu.`,

      pembayaran: `💳 **Metode Pembayaran:**\n\n• QRIS — scan dari aplikasi banking/e-wallet\n• Transfer Bank — virtual account\n• GoPay\n\n**Opsi:**\n• Bayar DP 30% — untuk mengunci jadwal\n• Bayar lunas — langsung lunas\n\nPelunasan wajib sebelum hari-H sesi foto.\nInvoice bisa diunduh dari Dashboard.`,

      lokasi: `📍 **Adaraimage Studio**\n\nJl. Studio No. 123, Jakarta\nBalaraja - Tangerang\n\nFasilitas:\n• Studio indoor lighting profesional\n• Backdrop premium (putih, hitam, vintage)\n• Ruang makeup & wardrobe\n• Area tunggu nyaman\n• Parkir luas`,

      kontak: `📞 **Hubungi Kami:**\n\n• WhatsApp: 0857-1251-5222\n• Email: adaraimage@gmail.com\n• Instagram: @adaraimage\n\nAtau chat di sini — admin akan membalas secepatnya.`,

      default: `Terima kasih atas pertanyaannya! 🙏\n\nUntuk informasi lebih detail, admin kami akan segera membantu. Sementara itu, Anda bisa:\n\n• Lihat portofolio di halaman Explore\n• Booking sesi di halaman Booking\n• Cek status pesanan di Dashboard\n\nAda yang bisa saya bantu? 😊`,
    },
  },
};

/** Build a fallback knowledge entry for a tenant that has no custom AI config. */
function buildFallbackKnowledge(tenantName: string): TenantAIKnowledge {
  return {
    tenantName,
    entries: {
      sapaan: `Halo! 👋 Saya AI Assistant ${tenantName}. Ada yang bisa saya bantu?\n\nAdmin kami siap membantu kapan saja.`,
      default: `Terima kasih atas pertanyaannya! 🙏\n\nAdmin kami akan segera membantu. Ada yang bisa saya bantu? 😊`,
    },
  };
}

function getKnowledge(tenantId: string): TenantAIKnowledge {
  const configured = TENANT_AI_KNOWLEDGE[tenantId];
  if (configured) return configured;

  // Fallback: if no custom KB, return a minimal generic one
  const friendlyName = tenantId.charAt(0).toUpperCase() + tenantId.slice(1);
  return buildFallbackKnowledge(friendlyName);
}

function getAIResponse(text: string, tenantId: string): string {
  const kb = getKnowledge(tenantId);
  const e = kb.entries;
  const l = text.toLowerCase();

  // Try the configured tenant's specific entries first
  if (l.includes("halo") || l.includes("hai") || l.includes("hi") || l.includes("selamat"))
    return e.sapaan ?? e.default ?? "Halo! Ada yang bisa kami bantu? 😊";
  if (l.includes("paket") || l.includes("wedding") || l.includes("prewed") || l.includes("photobox") || l.includes("video cinematic") || l.includes("komersial"))
    return e.paket ?? e.default ?? "Silakan tanyakan detail layanan kami.";
  if (l.includes("harga") || l.includes("rp") || l.includes("ribu") || l.includes("juta") || l.includes("biaya") || l.includes("berapa"))
    return e.harga ?? e.default ?? "Silakan tanyakan informasi harga.";
  if (l.includes("booking") || l.includes("pesan") || l.includes("jadwal") || l.includes("cara"))
    return e.booking ?? e.default ?? "Silakan tanyakan cara melakukan pemesanan.";
  if (l.includes("bayar") || l.includes("qris") || l.includes("transfer") || l.includes("dp") || l.includes("lunas") || l.includes("gopay"))
    return e.pembayaran ?? e.default ?? "Silakan tanyakan metode pembayaran.";
  if (l.includes("lokasi") || l.includes("alamat") || l.includes("dimana") || l.includes("studio") || l.includes("tangerang") || l.includes("jakarta"))
    return e.lokasi ?? e.default ?? "Silakan tanyakan lokasi kami.";
  if (l.includes("kontak") || l.includes("wa") || l.includes("whatsapp") || l.includes("telp") || l.includes("email") || l.includes("instagram"))
    return e.kontak ?? e.default ?? "Silakan tanyakan kontak kami.";

  return e.default ?? "Terima kasih! Admin kami akan segera membantu. 😊";
}

const gid = () => Math.random().toString(36).substring(2, 11);

/* ============================================================
   Tenant-aware greeting for new conversations
   ============================================================ */
export function getTenantGreeting(tenantId: string): string {
  const kb = getKnowledge(tenantId);
  return kb.entries.sapaan ?? `Halo! 👋 Selamat datang di ${kb.tenantName}. Ada yang bisa kami bantu?`;
}

/* ============================================================ */
interface ChatState {
  conversations: Conversation[];
  activeConversationId: string | null;
  isOpen: boolean;
  currentUserId: string;
  currentTenantId: string;
  isAdmin: boolean;

  setActiveConversation: (id: string | null) => void;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  sendMessage: (conversationId: string, text: string) => void;
  markAsRead: (conversationId: string) => void;
  addConversation: (conversation: Conversation) => void;
  toggleAI: (conversationId: string) => void;
  getUnreadTotal: () => number;
  getMyConversations: () => Conversation[];
  setAdminMode: (admin: boolean) => void;
  setCurrentUserId: (id: string) => void;
  setCurrentTenantId: (id: string) => void;
  ensureCustomerConversation: (customerId: string, customerName: string, tenantId: string) => void;
}

export const useChatStore = create<ChatState>((set, get) => ({
  conversations: [],
  activeConversationId: null,
  isOpen: false,
  currentUserId: "",
  currentTenantId: "adara", // backward-compat default
  isAdmin: false,

  setAdminMode: (admin) => set({ isAdmin: admin }),
  setCurrentUserId: (id) => set({ currentUserId: id }),
  setCurrentTenantId: (id) => set({ currentTenantId: id }),

  /* ─── Ensure customer has a conversation with admin ─── */
  ensureCustomerConversation: (customerId, customerName, tenantId) => {
    const s = get();
    const exists = s.conversations.some(
      (c) => c.customerId === customerId && c.tenantId === tenantId,
    );
    if (exists) return;

    const greeting = getTenantGreeting(tenantId);

    const conv: Conversation = {
      id: `conv-${tenantId}-${customerId}`,
      customerId,
      customerName,
      tenantId,
      lastMessage: greeting.split("\n")[0] || "Halo! Ada yang bisa kami bantu? 😊",
      lastMessageTime: Date.now(),
      unreadCount: 0,
      isOnline: true,
      isTyping: false,
      aiEnabled: true,
      messages: [{
        id: gid(),
        conversationId: `conv-${tenantId}-${customerId}`,
        senderId: "ai-agent",
        senderName: "AI Assistant",
        senderRole: "ai",
        text: greeting,
        timestamp: Date.now(),
        status: "sent",
      }],
    };
    set((st) => ({ conversations: [...st.conversations, conv] }));
  },

  getMyConversations: () => {
    const s = get();
    const tid = s.currentTenantId || "adara";

    let convs = s.isAdmin
      ? s.conversations.filter((c) => c.tenantId === tid)
      : s.conversations.filter(
          (c) => c.customerId === s.currentUserId && c.tenantId === tid,
        );
    return convs.sort((a, b) => b.lastMessageTime - a.lastMessageTime);
  },

  setActiveConversation: (id) => {
    set({ activeConversationId: id });
    if (id) get().markAsRead(id);
  },

  toggleChat: () => set((s) => ({ isOpen: !s.isOpen })),
  openChat: () => set({ isOpen: true }),
  closeChat: () => set({ isOpen: false }),

  toggleAI: (conversationId) => {
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === conversationId ? { ...c, aiEnabled: !c.aiEnabled } : c,
      ),
    }));
  },

  sendMessage: (conversationId, text) => {
    const s = get();
    const msg: ChatMessage = {
      id: gid(),
      conversationId,
      senderId: s.currentUserId,
      senderName: s.isAdmin ? "Admin" : "Anda",
      senderRole: s.isAdmin ? "admin" : "customer",
      text,
      timestamp: Date.now(),
      status: "sending",
    };

    // Add message
    set((st) => ({
      conversations: st.conversations.map((c) =>
        c.id === conversationId
          ? { ...c, lastMessage: text, lastMessageTime: Date.now(), messages: [...c.messages, msg] }
          : c,
      ),
    }));

    // Sent → Delivered
    setTimeout(() => set((st) => ({
      conversations: st.conversations.map((c) =>
        c.id === conversationId
          ? { ...c, messages: c.messages.map((m) => m.id === msg.id ? { ...m, status: "sent" as const } : m) }
          : c,
      ),
    })), 300);

    setTimeout(() => set((st) => ({
      conversations: st.conversations.map((c) =>
        c.id === conversationId
          ? { ...c, messages: c.messages.map((m) => m.id === msg.id ? { ...m, status: "delivered" as const } : m) }
          : c,
      ),
    })), 600);

    // AI auto-reply for customer messages (per-tenant aware)
    if (!s.isAdmin) {
      const conv = get().conversations.find((c) => c.id === conversationId);
      if (conv && conv.aiEnabled !== false) {
        const tid = conv.tenantId || s.currentTenantId || "adara";
        setTimeout(() => {
          set((st) => ({
            conversations: st.conversations.map((c) =>
              c.id === conversationId ? { ...c, isTyping: true } : c,
            ),
          }));
          setTimeout(() => {
            const reply = getAIResponse(text, tid);
            const aiMsg: ChatMessage = {
              id: gid(), conversationId,
              senderId: "ai-agent", senderName: "AI Assistant", senderRole: "ai",
              text: reply, timestamp: Date.now(), status: "sent",
            };
            set((st) => ({
              conversations: st.conversations.map((c) =>
                c.id !== conversationId ? c : {
                  ...c, isTyping: false,
                  lastMessage: reply, lastMessageTime: Date.now(),
                  unreadCount: st.activeConversationId === conversationId ? 0 : c.unreadCount + 1,
                  messages: [...c.messages, aiMsg],
                },
              ),
            }));
          }, 1500 + Math.random() * 1000);
        }, 1000);
      }
    }
  },

  markAsRead: (conversationId) => {
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === conversationId ? { ...c, unreadCount: 0 } : c,
      ),
    }));
  },

  addConversation: (conv) => {
    const s = get();
    // Ensure tenantId is set; fallback to current tenant for backward compat
    const tenantId = conv.tenantId || s.currentTenantId || "adara";
    set((st) => ({
      conversations: [{ ...conv, tenantId, aiEnabled: conv.aiEnabled ?? true }, ...st.conversations],
    }));
  },

  getUnreadTotal: () => {
    const s = get();
    const tid = s.currentTenantId || "adara";
    const convs = s.isAdmin
      ? s.conversations.filter((c) => c.tenantId === tid)
      : s.conversations.filter(
          (c) => c.customerId === s.currentUserId && c.tenantId === tid,
        );
    return convs.reduce((sum, c) => sum + c.unreadCount, 0);
  },
}));
